import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import Link from '@mui/material/Link';

import {
  useGetOrdersQuery,
} from '#services/order';
import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import {
  FULFILLMENT_KEY,
  FULFILLMENT_COLOR,
  FULFILLMENT_STATUS,
} from '#common/constants';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import DropdownFilterBox from '#components/DropdownFilterBox';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import {
  setFilter,
  setFilterKey,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSkipFlag,
} from './order.slice';

interface OrderListSlice {
  filter: string;
  filterKey: string;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableProps['rows'];
  rowCount: number;
  skip: boolean;
}

interface OrderRow {
  id?: string | number;
  createdDate?: string;
  fulfillmentStatus?: number;
  totalPrice?: number;
  [key: string]: unknown;
}

export default function UserOrders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('order');

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  const filterKey = useSelector((state: { orderList: OrderListSlice }) => state.orderList.filterKey);
  const filter = useSelector((state: { orderList: OrderListSlice }) => state.orderList.filter);
  const order = useSelector((state: { orderList: OrderListSlice }) => state.orderList.order);
  const orderBy = useSelector((state: { orderList: OrderListSlice }) => state.orderList.orderBy);
  const pageIndex = useSelector((state: { orderList: OrderListSlice }) => state.orderList.pageIndex);
  const pageSize = useSelector((state: { orderList: OrderListSlice }) => state.orderList.pageSize);
  const rows = useSelector((state: { orderList: OrderListSlice }) => state.orderList.rows);
  const rowCount = useSelector((state: { orderList: OrderListSlice }) => state.orderList.rowCount);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const skip = useSelector((state: { orderList: OrderListSlice }) => state.orderList.skip);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  // Sync filterForQuery with filter when filter changes
  useEffect(() => {
    setFilterForQuery(filter);
  }, [filter]);

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  useEffect(() => {
    // When filterForQuery changes, always trigger a search by setting skip to false
    dispatch(setSkipFlag(false));
  }, [filterForQuery, dispatch]);

  const query = useGetOrdersQuery({
    order,
    orderBy,
    filterKey,
    filter: filterForQuery,
    pageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
    skip,
  });

  // Sync filterForQuery with filter when filter changes
  useEffect(() => {
    setFilterForQuery(filter);
  }, [filter]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: t('table.orderNumber'),
    renderer: (row) => (
      <Link
        component="button"
        onClick={() => { void navigate(`/order/detail?orderId=${(row as OrderRow).id}`); }}
        sx={{ textDecoration: 'inherit' }}
      >
        {(row as OrderRow).id}
      </Link>
    ),
  }, {
    id: 'createdDate',
    label: t('table.orderDate'),
    renderer: (o) => {
      try {
        const { createdDate } = o as OrderRow;
        return formatDate(createdDate as unknown as Parameters<typeof formatDate>[0]);
      } catch (_err) {
        return t('unavailable');
      }
    },
  }, {
    id: 'fulfillmentStatus',
    label: t('table.orderStatus'),
    renderer: (o) => {
      const status = (o as OrderRow).fulfillmentStatus as number;
      return (
        <Chip
          size="small"
          label={t(`status.${FULFILLMENT_KEY[status as keyof typeof FULFILLMENT_KEY]}`)}
          color={FULFILLMENT_COLOR[status] as ChipProps['color']}
        />
      );
    },
  }, {
    id: 'totalPrice',
    label: t('table.orderTotal'),
    renderer: (o) => {
      const row = o as OrderRow;
      return (row.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? `${formatCurrency(0)}`
        : `${formatCurrency(row.totalPrice as unknown as Parameters<typeof formatCurrency>[0])}`;
    },
  }];

  const tableProps: EnhancedTableProps = {
    headers: tableHeaders,

    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    getRowStyle: (row) => (
      ((row as OrderRow).fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? { textDecoration: 'line-through' }
        : {}
    ),

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,
    rows,
  };

  return (
    <Box sx={{ py: { xs: 2, sm: 5 }, px: { xs: 2, sm: 0 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant={'h7' as TypographyProps['variant']} sx={{ fontWeight: 'fontWeightBold' as unknown as number }}>
          {t('title')}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('intro')}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('inStockNote')}
        </Typography>
      </Box>

      <Box
        sx={{ mb: 1 }}
      >
        <DropdownFilterBox
          width={'100%' as unknown as number}
          dropdownItems={[{
            value: 'orderId',
            text: t('filter.orderNumber'),
          }, {
            value: 'poNumber',
            text: t('filter.poNumber'),
          }, {
            value: 'style',
            text: t('filter.style'),
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder={t('filter.placeholder')}
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />
      </Box>

      <EnhancedTable
        name="user-list"
        {...tableProps}
      />

    </Box>

  );
}
